import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import { TextArea } from "@design-system/TextArea/TextArea";
import type {
    TextAreaHeightProps,
    TextAreaSize,
} from "@design-system/TextArea/TextArea.types";
import { pickChromeProps } from "./kitChromeProps";
import type { KitFieldProps } from "./kitFieldProps.types";
import type { TextFieldPath } from "./TextField";

type TextAreaFieldValue = string | null | undefined;

export type TextAreaFieldProps<Name extends string> = KitFieldProps<
    Name,
    HTMLTextAreaElement,
    string
> &
    TextAreaHeightProps & {
        readOnly?: boolean;
        size?: TextAreaSize;
        placeholder?: string;
        rows?: number;
        maxLength?: number;
    };

const toHeightProps = (props: TextAreaHeightProps): TextAreaHeightProps =>
    props.autoGrow === true
        ? { autoGrow: true, minRows: props.minRows, maxRows: props.maxRows }
        : { resizable: props.resizable };

/**
 * Multiline text field bound to a typed Formik path. `null` or `undefined`
 * values show as empty text; edits always write a string.
 */
export const TextAreaField = <
    Values,
    Name extends TextFieldPath<Values> = TextFieldPath<Values>,
>(
    props: TextAreaFieldProps<Name>,
) => {
    const {
        name,
        ref,
        disabled,
        readOnly,
        required,
        onValueChange,
        size,
        placeholder,
        rows,
        maxLength,
    } = props;

    return (
        <FormikField<TextAreaFieldValue>
            name={name}
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => (
                <TextArea
                    {...toHeightProps(props)}
                    ref={ref}
                    name={name}
                    value={field.value ?? ""}
                    onValueChange={(value) => {
                        void helpers.setValue(value);
                        onValueChange?.(value);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    invalid={invalid}
                    aria-required={required || undefined}
                    {...ariaLabelledbyProps(props["aria-labelledby"])}
                    disabled={disabled}
                    readOnly={readOnly}
                    size={size}
                    placeholder={placeholder}
                    rows={rows}
                    maxLength={maxLength}
                />
            )}
        </FormikField>
    );
};
